import { toast } from 'react-toastify';

export interface GeneratedImagePool {
  messageId: string;
  images: Array<{
    id: string;
    uploadType: string;
    fileType: string;
    fileUrl: string;
    isAiGeneratedJustNow?: boolean;
  }>;
  selectedIds: string[];
  generatedAt: number;
}

/**
 * Save AI-generated image pool to sessionStorage
 * @param projectId - The project ID to use as storage key
 * @param aiPool - The AI pool data to save, or undefined to remove
 * @returns true if successful, false otherwise
 */
export const saveAIPoolToSession = (
  projectId: string,
  aiPool: GeneratedImagePool | undefined
): boolean => {
  try {
    if (aiPool) {
      sessionStorage.setItem(`aiPool_${projectId}`, JSON.stringify(aiPool));
    } else {
      sessionStorage.removeItem(`aiPool_${projectId}`);
    }
    return true;
  } catch (error) {
    console.error('Failed to save AI pool to sessionStorage:', error);

    if (error instanceof Error && error.name === 'QuotaExceededError') {
      toast.info(
        'Unable to save AI images for later editing. Your selected images will still be saved to the project.',
        { autoClose: 5000 }
      );
    }

    return false;
  }
};

/**
 * Load AI-generated image pool from sessionStorage
 * @param projectId - The project ID to use as storage key
 * @returns The AI pool data if found, undefined otherwise
 */
export const loadAIPoolFromSession = (
  projectId: string
): GeneratedImagePool | undefined => {
  try {
    const stored = sessionStorage.getItem(`aiPool_${projectId}`);
    if (stored) {
      return JSON.parse(stored) as GeneratedImagePool;
    }
  } catch (error) {
    console.error('Failed to load AI pool from sessionStorage:', error);
  }
  return undefined;
};